import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { SEARCH_DEBOUNCE_MILLISECONDS, ADMIN_LIST_PAGE_SIZE } from '@/constants/adminPagination';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  usePractitionerChatConversationByIdQuery,
  usePractitionerChatConversationsQuery,
} from '@/hooks/useChat';
import { ConversationList } from '@/components/chat/ConversationList';
import { ConversationScreen } from '@/components/chat/ConversationScreen';
import { NewConversationDialog } from '@/components/chat/NewConversationDialog';

function findConversationById(
  conversations: ReturnType<typeof usePractitionerChatConversationsQuery>['data'],
  conversationId: string | undefined
) {
  if (conversations === undefined || conversationId === undefined) {
    return null;
  }
  const matchingConversation = conversations.items.find(function matches(conversation) {
    return conversation.id === conversationId;
  });
  return matchingConversation === undefined ? null : matchingConversation;
}

export function ChatPage() {
  const { t } = useTranslation('chat');
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId: string }>();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isNewConversationOpen, setIsNewConversationOpen] = useState(false);
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MILLISECONDS);
  const conversationsQuery = usePractitionerChatConversationsQuery(page, debouncedSearch);
  const conversations = conversationsQuery.data === undefined ? [] : conversationsQuery.data.items;
  const totalConversations =
    conversationsQuery.data === undefined ? 0 : conversationsQuery.data.totalItems;

  useEffect(
    function resetPageOnSearchChange() {
      setPage(1);
    },
    [debouncedSearch]
  );
  const activeConversationId = conversationId === undefined ? null : conversationId;
  const activeConversationFromList = findConversationById(conversationsQuery.data, conversationId);
  const shouldFetchActiveConversationById =
    activeConversationId !== null &&
    activeConversationFromList === null &&
    conversationsQuery.isLoading === false;
  const activeConversationByIdQuery = usePractitionerChatConversationByIdQuery(
    shouldFetchActiveConversationById ? activeConversationId : null
  );
  const activeConversation =
    activeConversationFromList !== null
      ? activeConversationFromList
      : activeConversationByIdQuery.data === undefined
        ? null
        : activeConversationByIdQuery.data;

  return (
    <PageContainer width="full">
      <div className="flex flex-col h-[calc(100dvh-13rem)] min-h-[480px]">
        <div className="flex items-center justify-between mb-4 shrink-0">
          <h1 className="text-2xl font-bold text-zooindigo">{t('chat:list.title')}</h1>
          <Button
            type="button"
            variant="outline"
            size="small"
            onClick={function openNewConversation() {
              setIsNewConversationOpen(true);
            }}
          >
            <Icon name="Plus" />
            {t('chat:list.newConversation')}
          </Button>
        </div>

        <div className="grid grid-cols-1 grid-rows-[minmax(0,1fr)] md:grid-cols-[320px_1fr] gap-4 flex-1 min-h-0 rounded-2xl border border-zooborder overflow-hidden">
          <div
            className={
              activeConversationId === null
                ? 'flex flex-col min-h-0'
                : 'hidden md:flex md:flex-col md:min-h-0'
            }
          >
            <ConversationList
              conversations={conversations}
              activeConversationId={activeConversationId}
              onSelect={function selectConversation(conversation) {
                if (conversation.id !== null) {
                  navigate(APPLICATION_ROUTES.workspaceChat + '/' + conversation.id);
                }
              }}
              isLoading={conversationsQuery.isLoading}
              fallbackCounterpartName={t('chat:list.title')}
              searchValue={search}
              onSearchChange={setSearch}
              page={page}
              pageSize={ADMIN_LIST_PAGE_SIZE}
              totalItems={totalConversations}
              onPageChange={setPage}
            />
          </div>
          <div
            className={
              activeConversationId === null
                ? 'hidden md:flex md:flex-col md:min-h-0'
                : 'flex flex-col min-h-0'
            }
          >
            {activeConversationId !== null &&
            activeConversation === null &&
            shouldFetchActiveConversationById === true &&
            activeConversationByIdQuery.isLoading === true ? (
              <div className="flex h-full items-center justify-center">
                <LoadingSpinner className="w-6 h-6" />
              </div>
            ) : activeConversationId === null || activeConversation === null ? (
              <div className="hidden md:flex h-full items-center justify-center text-sm text-zoomuted">
                {t('chat:conversation.emptyBody')}
              </div>
            ) : (
              <ConversationScreen
                scope="practitioner"
                conversationId={activeConversationId}
                counterpartName={activeConversation.counterpartName}
                fallbackCounterpartName={t('chat:list.title')}
                ownSenderKind="practitioner"
                isReadOnly={activeConversation.status === 'archived'}
                readOnlyNotice={t('chat:conversation.archivedNotice')}
                onBack={function backToList() {
                  navigate(APPLICATION_ROUTES.workspaceChat);
                }}
              />
            )}
          </div>
        </div>
      </div>

      {isNewConversationOpen === false ? null : (
        <NewConversationDialog
          onClose={function closeDialog() {
            setIsNewConversationOpen(false);
          }}
          onConversationStarted={function openStartedConversation(newConversationId) {
            setIsNewConversationOpen(false);
            navigate(APPLICATION_ROUTES.workspaceChat + '/' + newConversationId);
          }}
        />
      )}
    </PageContainer>
  );
}
